import { Suspense, useEffect, useState, type ReactNode } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { HuddleProvider } from "@huddle01/react";

import { useIsMobile } from "@/hooks/use-is-mobile";
import { usePresenceHeartbeat } from "@/hooks/use-presence-heartbeat";
import { registerTypingPublisher } from "@/realtime/typing-publisher";
import { useSlumbrNotifications } from "@slumbr/app-sdk";

// One-time install of the direct-Momento typing publisher. Runs at module
// load so any component calling `sendChatTyping` from @slumbr/core gets
// the real implementation instead of the silent no-op fallback.
registerTypingPublisher();
import { TopBar } from "./top-bar";
import { DockDesktop } from "./dock/dock-desktop";
import { DockMobile } from "./dock/dock-mobile";
import { hidesAppBarOnTouch, isFullBleedRoute } from "./full-bleed-routes";
import { registerShellDockWidgets } from "./dock/register-shell-widgets";
registerShellDockWidgets();
import { installRoomSessionTeardown } from "@/apps/games/kit/session-teardown";
// Game seats outlive their room pages; end them when the identity changes.
installRoomSessionTeardown();
import { NotificationPanel } from "./notification-panel";
import { MobileBottomNav } from "./mobile-bottom-nav";
import { IncomingCallOverlay } from "@/apps/messenger/components/incoming-call-overlay";
import { CallOverlay } from "@/apps/messenger/components/call-overlay";
import { getHuddleClient } from "@/apps/messenger/huddle01/config";

const DEV_AUTH_BYPASS = import.meta.env.VITE_DEV_AUTH_BYPASS === "true";

const LoadingSpinner = () => (
  <div className="flex h-full items-center justify-center">
    <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
  </div>
);

/**
 * Wraps the shell tree in HuddleProvider so the CallOverlay (which lives at
 * shell level for cross-app persistence) can use Huddle01 hooks.
 * Skipped in DEV_AUTH_BYPASS mode where calls are mocked.
 */
function ShellHuddleProvider({ children }: { children: ReactNode }) {
  if (DEV_AUTH_BYPASS) return <>{children}</>;
  return <HuddleProvider client={getHuddleClient()}>{children}</HuddleProvider>;
}

export function ShellLayout() {
  return (
    <ShellHuddleProvider>
      <ShellLayoutInner />
    </ShellHuddleProvider>
  );
}

function ShellLayoutInner() {
  const isMobile = useIsMobile();
  const location = useLocation();
  // Hide the global bottom nav when the user is inside a chat conversation so
  // the conversation can fill the viewport (matches WhatsApp / iMessage).
  const inChatConversation = /^\/messenger\/[^/]+/.test(location.pathname);
  // Full-bleed game routes (Mimic pass-and-play, Card Table rooms) hide the dock and the mobile
  // bottom nav: the page owns the whole viewport and has its own exit.
  const fullBleed = isFullBleedRoute(location.pathname);
  const noAppBarOnTouch = hidesAppBarOnTouch(location.pathname);

  useSlumbrNotifications();
  usePresenceHeartbeat();

  // Hide the mobile widget dock in landscape (it overlaps full-screen landscape
  // games); the dock returns in portrait.
  const [mobileLandscape, setMobileLandscape] = useState(
    () => typeof matchMedia !== "undefined" && matchMedia("(pointer: coarse) and (orientation: landscape)").matches,
  );
  useEffect(() => {
    const mq = matchMedia("(pointer: coarse) and (orientation: landscape)");
    const onChange = () => setMobileLandscape(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  // Touch devices wide enough for the desktop tree (landscape phones, tablets) also drop the top
  // bar in Card Table rooms: a 390 px-tall landscape phone can't spare it (the page has its own bar).
  const [coarsePointer, setCoarsePointer] = useState(() => typeof matchMedia !== "undefined" && matchMedia("(pointer: coarse)").matches);
  useEffect(() => {
    const mq = matchMedia("(pointer: coarse)");
    const onChange = () => setCoarsePointer(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // Embedded mode (`?embed=1`) — the native app loads a game route inside a
  // WebView. Render only the routed content (no top bar / sidebar / bottom
  // nav), so the embedded game owns the full viewport. HuddleProvider + the
  // overlays still come from the outer ShellLayout wrapper.
  const embed = new URLSearchParams(location.search).get("embed") === "1";
  if (embed) {
    return (
      <div className="h-[100dvh] max-h-[100dvh] overflow-hidden bg-surface-0 dark:bg-surface-dark-0">
        <Suspense fallback={<LoadingSpinner />}>
          <Outlet />
        </Suspense>
      </div>
    );
  }

  if (isMobile) {
    return (
      <div className="flex h-[100dvh] max-h-[100dvh] flex-col overflow-hidden bg-surface-0 dark:bg-surface-dark-0">
        {/* Mobile: scrollable content sandwiched between persistent bar + bottom nav (both fixed by flex layout).
            The bottom nav pads the home indicator, so content below it needn't (see games kit SAFE_BOTTOM). */}
        <main className="min-h-0 flex-1 overflow-auto [--shell-safe-bottom:0px]">
          <Suspense fallback={<LoadingSpinner />}>
            <Outlet />
          </Suspense>
        </main>
        {!inChatConversation && !mobileLandscape && !fullBleed && <DockMobile />}
        {!inChatConversation && !fullBleed && <MobileBottomNav />}
        <NotificationPanel />
        <IncomingCallOverlay />
        <CallOverlay />
      </div>
    );
  }

  return (
    <div className="flex h-[100dvh] max-h-[100dvh] flex-col overflow-hidden bg-surface-0 dark:bg-surface-dark-0">
      {/* Desktop: unified top bar + content */}
      {!(noAppBarOnTouch && coarsePointer) && <TopBar />}
      <div className="flex min-h-0 flex-1 overflow-hidden">
        <main className="flex-1 overflow-auto">
          <Suspense fallback={<LoadingSpinner />}>
            <Outlet />
          </Suspense>
        </main>
      </div>
      {!fullBleed && <DockDesktop />}
      <NotificationPanel />
      <IncomingCallOverlay />
      <CallOverlay />
    </div>
  );
}
